import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { showEditWarrant } from 'reducers/PersonDialogsReducer';
import RecordBookingDetails from 'components/RMSRecordsDialogs/EditPersonDialog/RecordBookingDetails';
import IncEventDetails from 'components/RMSForms/RMSIncidentForm/IncEventDetails';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  CATEGORY_LABELS,
  CATEGORY_ROW_ID,
  CATEGORY_COLUMNS,
} from '../../constants/personRelatedRecordDetailFormConstants';
import {
  RECORD_CATEGORY_KEYS,
  VIEW_PERMISSION,
} from '../../constants/personRelatedRecordsTabConstants';
import { getViewPermission } from '../../utility/personRelatedRecordDetailHelper';

const PersonRelatedRecordDetailDialog = ({ open, category, rows, onClose, onNavigateAway }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const permissions = useSelector((state) => state.permissions);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const viewPermission = getViewPermission(category, permissions);

  const handleConfirmNavigate = useCallback(
    (path) => {
      onNavigateAway?.();
      onClose();
      history.push(path);
    },
    [onNavigateAway, onClose, history]
  );

  const handleView = useCallback(
    (row) => {
      if (category === RECORD_CATEGORY_KEYS.WARRANTS) {
        dispatch(showEditWarrant(row));
        return;
      }

      if (category === RECORD_CATEGORY_KEYS.RMS_INCIDENTS) {
        requestConfirm(
          'Do you wish to proceed?',
          () => handleConfirmNavigate(`/incidents/edit/${row.ptsIncidentID}`),
          'This record is going to be closed!'
        );
        return;
      }

      if (category === RECORD_CATEGORY_KEYS.CITATIONS) {
        requestConfirm(
          'Do you wish to proceed?',
          () => handleConfirmNavigate(`/citations/edit/${row.ptsCitationID}`),
          'This record is going to be closed!'
        );
        return;
      }

      if (category === RECORD_CATEGORY_KEYS.CAD_INCIDENTS) {
        setSelectedEvent(row);
        setEventDialogOpen(true);
      }
    },
    [category, dispatch, requestConfirm, handleConfirmNavigate]
  );

  const handleCloseEventDialog = useCallback(() => setEventDialogOpen(false), []);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={CATEGORY_LABELS[category] || 'Related Records'}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        saveButton={{ show: false }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}>
        {category === RECORD_CATEGORY_KEYS.BOOKINGS ? (
          <RecordBookingDetails data={rows} />
        ) : (
          <CommonDataGrid
            rows={rows}
            columns={CATEGORY_COLUMNS[category] || []}
            getRowId={(row) => row[CATEGORY_ROW_ID[category]] || row.id}
            autoHeight
            toolbar={{
              showSearch: true,
              showColumnSelector: true,
              showFilter: true,
              showDensity: true,
              showExport: true,
            }}
            actions={{
              custom: viewPermission
                ? [
                    {
                      icon:
                        viewPermission === VIEW_PERMISSION.EDIT ? <EditIcon /> : <VisibilityIcon />,
                      tooltip: `${viewPermission} ${CATEGORY_LABELS[category] || category}`,
                      onClick: handleView,
                    },
                  ]
                : [],
            }}
          />
        )}
      </RMSCommonDialog>

      {eventDialogOpen && (
        <RMSCommonDialog
          open={eventDialogOpen}
          title="Event Details"
          onClose={handleCloseEventDialog}
          maxWidth="md"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}>
          <IncEventDetails
            event={{ Event: selectedEvent }}
            ptsEventID={selectedEvent?.ptsEventID}
          />
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default PersonRelatedRecordDetailDialog;
